import { Head, Link, router, usePage } from '@inertiajs/react'
import { useState, useEffect, useRef, useCallback, Component, ReactNode } from 'react'
import {
  User, Meeting, MeetingSection, ActionItem, Topic, Rock, RockCategory,
  AgendaTemplateSummary, AvailableSection, MeetingAttendee, CascadingMessage, MeetingRating,
  SectionType, SECTION_DISPLAY_NAMES, SharedProps, ScoreboardSectionProps, TopicResolution, MeetingRock, RockReviewSectionProps
} from '../../types'
import UserAvatar from '../../components/shared/UserAvatar'
import Layout from '../../components/Layout'
import QuickActionsToolbar from '../../components/meetings/QuickActionsToolbar'
import { MeetingCreationContext } from '../../components/meetings/MeetingTopicActions'
import MeetingSetup from '../../components/meetings/MeetingSetup'
import SectionSetTheStage, { type SetStageVision } from '../../components/meetings/SectionSetTheStage'
import SectionCheckIn from '../../components/meetings/SectionCheckIn'
import SectionScoreboard from '../../components/meetings/SectionScoreboard'
import SectionRockReview from '../../components/meetings/SectionRockReview'
import SectionAccountability from '../../components/meetings/SectionAccountability'
import SectionTeamEngagement from '../../components/meetings/SectionTeamEngagement'
import SectionBizDev from '../../components/meetings/SectionBizDev'
import SectionTheList from '../../components/meetings/SectionTheList'
import SectionProjects from './_sections/SectionProjects'
import SectionCheckOut from '../../components/meetings/SectionCheckOut'
import SectionNotes from '../../components/meetings/SectionNotes'
import ScoreboardSectionNotes from '../../components/meetings/ScoreboardSectionNotes'

interface Props {
  meeting: Meeting
  sections: MeetingSection[]
  set_stage_vision: SetStageVision | null
  scoreboard_data: ScoreboardSectionProps
  rocks: Rock[]
  rock_categories?: RockCategory[]
  can_create_rock?: boolean
  priorities: MeetingRock[]
  quarter_label: string
  quarter_end_date: string
  action_items: ActionItem[]
  topics: Topic[]
  topic_vote_count?: number
  new_action_items: ActionItem[]
  team_members: User[]
  action_item_members: User[]
  meeting_attendees?: MeetingAttendee[]
  can_create_action_item?: boolean
  can_facilitate: boolean
  can_start: boolean
  can_save_as_template?: boolean
  server_time?: number
  is_last_section?: boolean
  templates?: AgendaTemplateSummary[]
  available_sections?: AvailableSection[]
  cascading_messages?: CascadingMessage[]
  meeting_ratings?: MeetingRating[]
  meeting_subscribers?: { id: number; user_id: number; user_name: string }[]
  summary_recipients?: { id: number; name: string; email: string }[]
  organization_teams?: { id: number; name: string }[]
  completed_this_week?: ActionItem[]
  completed_this_meeting?: ActionItem[]
  meeting_topic_resolutions?: TopicResolution[]
  viewer_count?: number
  connected_user_ids?: number[]
}

const HEARTBEAT_INTERVAL = 10000

class SectionErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }
  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[Meeting] Section render error:', error.message, info.componentStack)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-[rgba(237,28,36,0.15)] border border-[#ED1C24]/20 rounded-lg p-6 text-center">
          <h4 className="text-sm font-semibold text-[#ED1C24] mb-2">Something went wrong rendering this section</h4>
          <p className="text-sm text-[#ED1C24] mb-3">{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="text-sm text-[#ED1C24] underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatCountdown(seconds: number): string {
  const sign = seconds < 0 ? '-' : ''
  const abs = Math.abs(seconds)
  const hours = Math.floor(abs / 3600)
  const mins = Math.floor((abs % 3600) / 60)
  const secs = abs % 60
  if (hours > 0) {
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${sign}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const FALLBACK_SECTION_NAMES: Record<string, string> = {
  set_the_stage: 'Set the Stage',
  check_in: 'Check-In',
  scoreboard: 'Scoreboard',
  rock_review: 'Rock Review',
  accountability: 'Accountability from Last Week',
  team_engagement: 'Team Engagement',
  business_development: 'Business Development',
  the_list: 'The List of Topics',
  projects: 'Projects',
  check_out: 'Check-Out',
}

function fallbackSectionDisplayName(key: string): string {
  return FALLBACK_SECTION_NAMES[key] || key.replace(/_/g, ' ')
}

function sectionDisplayNameFor(sections: MeetingSection[], key: string): string {
  const section = sections.find(s => s.section_type === key)
  return section?.display_name || fallbackSectionDisplayName(key)
}

// Task #267: every row — including multiple `custom` rows that share a
// `section_type` — must show its OWN title, not the first match looked
// up by section_type. Use the per-row display_name/title and fall back
// only when the row carries no human-readable label at all.
function sectionRowDisplayName(section: MeetingSection): string {
  return section.display_name || section.title || fallbackSectionDisplayName(section.section_type)
}

function sectionDuration(key: string): string {
  const durations: Record<string, string> = {
    set_the_stage: '3 min',
    check_in: '5 min',
    scoreboard: '5 min',
    rock_review: '5 min',
    accountability: '5 min',
    team_engagement: '5 min',
    business_development: '5 min',
    the_list: '50 min',
    projects: '10 min',
    check_out: '7 min',
  }
  return durations[key] || '5 min'
}

export default function MeetingShow({
  meeting, sections, set_stage_vision, scoreboard_data, rocks, rock_categories, can_create_rock, priorities, quarter_label, quarter_end_date,
  action_items, topics, topic_vote_count, new_action_items, team_members, action_item_members, meeting_attendees,
  can_create_action_item, can_facilitate, can_start, can_save_as_template, server_time, is_last_section, templates, available_sections,
  cascading_messages, meeting_ratings, meeting_subscribers, summary_recipients, organization_teams, completed_this_week, completed_this_meeting,
  meeting_topic_resolutions,
  viewer_count: viewerCount = 1, connected_user_ids: connectedUserIds = []
}: Props) {
  const pageProps = usePage().props as unknown as SharedProps
  const current_user = pageProps.current_user
  // Task #261: heading rows are visual labels — they have no timer, no
  // phase, and aren't part of the navigable section order.
  const timedSections = sections.filter(s => !s.is_heading)
  const sectionOrder: SectionType[] = timedSections.map(s => s.section_type)

  const initSectionTimes: Record<number, number> = {}
  sections.forEach(s => { initSectionTimes[s.id] = s.elapsed_seconds || 0 })

  const runningSections: Record<number, boolean> = {}
  sections.forEach(s => { runningSections[s.id] = s.timer_running })

  const [liveSectionTimes, setLiveSectionTimes] = useState<Record<number, number>>(initSectionTimes)
  // Task #261: track the section the facilitator is *viewing* by id so
  // we can disambiguate multiple rows that share section_type='custom'.
  const [viewingSectionId, setViewingSectionId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(meeting.display_name || '')
  const [timeWarning, setTimeWarning] = useState<{ type: 'warning' | 'over'; sectionName: string } | null>(null)
  const warningDismissedRef = useRef<string | null>(null)

  const lastHeartbeatRef = useRef<number>(Date.now())
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    const newTimes: Record<number, number> = {}
    sections.forEach(s => { newTimes[s.id] = s.elapsed_seconds || 0 })
    setLiveSectionTimes(newTimes)
  }, [sections.map(s => `${s.id}:${s.elapsed_seconds}:${s.timer_running}`).join(',')])

  useEffect(() => {
    if (meeting.status !== 'in_progress') return
    const interval = setInterval(() => {
      setLiveSectionTimes(prev => {
        const next = { ...prev }
        sections.forEach(s => {
          if (s.timer_running) {
            next[s.id] = (next[s.id] || 0) + 1
          }
        })
        return next
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [meeting.status, sections.map(s => `${s.id}:${s.timer_running}`).join(',')])

  const activeSectionIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (meeting.status !== 'in_progress') {
      setTimeWarning(null)
      return
    }
    const activeSection = sections.find(s => s.timer_running)
    if (!activeSection) {
      setTimeWarning(null)
      return
    }

    if (activeSectionIdRef.current !== activeSection.id) {
      activeSectionIdRef.current = activeSection.id
      warningDismissedRef.current = null
      setTimeWarning(null)
    }

    const elapsed = liveSectionTimes[activeSection.id] || 0
    const allocated = activeSection.duration_minutes * 60
    const remaining = allocated - elapsed
    const warningKey = `${activeSection.id}`

    if (remaining <= 0) {
      if (warningDismissedRef.current !== `${warningKey}-over`) {
        setTimeWarning({ type: 'over', sectionName: activeSection.display_name || fallbackSectionDisplayName(activeSection.section_type) })
      }
    } else if (remaining <= 30) {
      if (warningDismissedRef.current !== `${warningKey}-warning` && warningDismissedRef.current !== `${warningKey}-over`) {
        setTimeWarning({ type: 'warning', sectionName: activeSection.display_name || fallbackSectionDisplayName(activeSection.section_type) })
      }
    } else {
      setTimeWarning(null)
    }
  }, [liveSectionTimes, meeting.status, sections.map(s => `${s.id}:${s.timer_running}`).join(',')])

  const dismissWarning = useCallback(() => {
    const activeSection = sections.find(s => s.timer_running)
    if (activeSection) {
      const elapsed = liveSectionTimes[activeSection.id] || 0
      const allocated = activeSection.duration_minutes * 60
      warningDismissedRef.current = `${activeSection.id}-${elapsed >= allocated ? 'over' : 'warning'}`
    }
    setTimeWarning(null)
  }, [sections, liveSectionTimes])

  const sectionTimes = liveSectionTimes
  const totalMeetingTime = Object.values(liveSectionTimes).reduce((sum, t) => sum + t, 0)
  const totalAllottedSeconds = timedSections.reduce((sum, s) => sum + (s.duration_minutes || 0) * 60, 0)
  const remainingSeconds = totalAllottedSeconds - totalMeetingTime

  const isCompleted = meeting.status === 'completed'
  const currentPhase = meeting.current_phase
  // Task #261: id-based section tracking so multiple `custom` rows
  // resolve unambiguously. `currentSectionId` comes from the server's
  // in-progress row; for completed meetings we default to the first
  // timed section.
  const serverCurrentSectionId = meeting.current_section_id ?? null
  const currentSectionFromId = serverCurrentSectionId
    ? sections.find(s => s.id === serverCurrentSectionId) || null
    : null
  const currentSectionFromPhase = currentPhase
    ? sections.find(s => !s.is_heading && s.section_type === currentPhase) || null
    : null
  const currentSection = currentSectionFromId || currentSectionFromPhase
  const currentSectionId = currentSection?.id ?? null

  const displaySectionId = isCompleted
    ? (viewingSectionId ?? timedSections[0]?.id ?? null)
    : (viewingSectionId ?? currentSectionId)
  const displaySection = displaySectionId
    ? sections.find(s => s.id === displaySectionId) || null
    : null
  const displayPhase: SectionType | null = displaySection?.section_type ?? null
  const displaySectionIndex = displaySection
    ? timedSections.findIndex(s => s.id === displaySection.id)
    : -1

  // Inertia cancels any in-flight visit when a new one starts, so a heartbeat
  // firing mid-save would cancel a user-initiated visit (e.g. Convert to
  // Action Item), stranding its onSuccess/onError. Track whether a
  // non-heartbeat visit is in flight and skip the heartbeat tick while so;
  // the next tick (10s later) picks it back up. The reverse direction — a
  // user visit cancelling an in-flight heartbeat — is harmless.
  const userVisitInFlightRef = useRef(false)
  useEffect(() => {
    const isHeartbeat = (visit: { url: URL }) => visit.url.pathname.endsWith('/heartbeat')
    const offStart = router.on('start', (event) => {
      if (!isHeartbeat(event.detail.visit)) userVisitInFlightRef.current = true
    })
    // 'finish' fires for completed, errored, AND cancelled visits, so the
    // flag can't get stuck.
    const offFinish = router.on('finish', (event) => {
      if (!isHeartbeat(event.detail.visit)) userVisitInFlightRef.current = false
    })
    return () => {
      offStart()
      offFinish()
    }
  }, [])

  const sendHeartbeat = useCallback(() => {
    if (meeting.status !== 'in_progress') return
    if (userVisitInFlightRef.current) return

    // Heartbeat is server-authoritative: the active section is always
    // the meeting's current phase. We do not send any client-side
    // override — only the facilitator's jump action can change phase.
    //
    // The scoreboard cadence (task #462 tabs) and the number of periods
    // shown (task #472 for non-weekly, task #707 for the weekly 4/8/12
    // window) live in the URL query. The heartbeat's redirect re-serializes
    // the full page props, so it must carry both through — otherwise every
    // heartbeat would reset scoreboard_data back to the weekly 4-week
    // default while another tab or a wider window is selected.
    const query = new URLSearchParams(window.location.search)
    const cadence = query.get('scoreboard_cadence')
    const periods = query.get('scoreboard_periods')
    const heartbeatBody: Record<string, string> = {}
    if (cadence) heartbeatBody.scoreboard_cadence = cadence
    if (periods) heartbeatBody.scoreboard_periods = periods
    router.post(`/meetings/${meeting.id}/heartbeat`, heartbeatBody, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        lastHeartbeatRef.current = Date.now()
      },
    })
  }, [meeting.id, meeting.status])

  useEffect(() => {
    if (meeting.status !== 'in_progress') return

    sendHeartbeat()

    heartbeatRef.current = setInterval(() => sendHeartbeat(), HEARTBEAT_INTERVAL)

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    }
  }, [meeting.status, sendHeartbeat])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && meeting.status === 'in_progress') {
        sendHeartbeat()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [meeting.status, sendHeartbeat])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  }

  const handleSaveDisplayName = () => {
    router.put(`/meetings/${meeting.id}`, {
      display_name: nameValue.trim()
    }, {
      preserveScroll: true,
      onSuccess: () => setEditingName(false),
    })
  }

  const handleAdvanceSection = () => {
    setViewingSectionId(null)
    router.post(`/meetings/${meeting.id}/advance_section`)
  }

  const handleEndMeeting = () => {
    if (confirm('This will end the meeting for all participants. Continue?')) {
      router.post(`/meetings/${meeting.id}/end_meeting`)
    }
  }

  // Task #261: id-based so two `custom` rows don't both register as
  // current. Falls back to status/section_type for legacy paths.
  const getSectionStatusForSection = (section: MeetingSection | undefined) => {
    if (!section) return 'upcoming'
    if (meeting.status === 'scheduled') return 'upcoming'
    if (meeting.status === 'completed') return 'completed'
    if (currentSectionId && section.id === currentSectionId) return 'current'
    if (section.status === 'completed') return 'completed'
    return 'upcoming'
  }

  const isOverTime = (section: MeetingSection | undefined) => {
    if (!section) return false
    const elapsed = sectionTimes[section.id] || 0
    const allocated = section.duration_minutes * 60
    return elapsed > allocated
  }

  const handleSidebarClick = (section: MeetingSection) => {
    if (isCompleted) {
      setViewingSectionId(section.id)
      return
    }

    if (can_facilitate && meeting.status === 'in_progress') {
      if (section.id === currentSectionId) return
      setViewingSectionId(null)
      // Task #261: send section_id so multiple `custom` rows can be
      // addressed individually.
      router.post(
        `/meetings/${meeting.id}/jump_to_section`,
        { section_id: section.id },
        { preserveScroll: true }
      )
      return
    }

    const status = getSectionStatusForSection(section)
    if (status === 'completed') {
      setViewingSectionId(section.id)
    } else if (status === 'current') {
      setViewingSectionId(null)
    }
  }

  // Task #267: known built-in phases get their dedicated renderer.
  // Anything else (a `custom` row, or an unrecognized section_type that
  // a template might inject) falls through to the simple "title + timer
  // + notes" body — so the active panel never shows "Unknown section"
  // and every row gets a notes capture surface.
  const KNOWN_BUILT_IN_PHASES = new Set<string>([
    'set_the_stage', 'check_in', 'scoreboard', 'rock_review',
    'accountability', 'team_engagement', 'business_development',
    'the_list', 'projects', 'check_out',
  ])

  // Task #387: Check-In and Team Engagement should only show people who
  // are actually attending, matching Check-Out. When attendance records
  // exist, keep attending-only users; otherwise fall back to all active
  // non-guide team members (team_members already excludes guides).
  // Task #613: do NOT alphabetize. This feeds Check-In / Set the Stage, which
  // deliberately follow the facilitator's custom attendee order.
  const attendingMembers = (meeting_attendees && meeting_attendees.length > 0)
    ? meeting_attendees.filter(a => a.attending).map(a => a.user)
    : team_members

  const renderSectionBody = (phase: string) => {
    switch (phase) {
      case 'set_the_stage':
        return <SectionSetTheStage members={team_members} meeting={meeting} meetingAttendees={meeting_attendees} canFacilitate={can_facilitate} vision={set_stage_vision} sectionNumber={displaySectionIndex + 1} sectionCount={timedSections.length} durationMinutes={displaySection?.duration_minutes ?? 5} />
      case 'check_in':
        return <SectionCheckIn members={attendingMembers} readOnly={isCompleted} />
      case 'scoreboard':
        return <SectionScoreboard {...scoreboard_data} meeting_id={meeting.id} can_facilitate={can_facilitate} teamMembers={team_members} canCreateTopic={can_facilitate && !isCompleted && meeting.status === 'in_progress'} currentUserId={current_user?.id} />
      case 'rock_review':
        return <SectionRockReview rocks={rocks || []} meetingId={meeting.id} teamId={meeting.team?.id || 0} teamMembers={team_members} rockCategories={rock_categories || []} canCreateRock={can_create_rock === true} currentUserId={current_user?.id} quarter_label={quarter_label || ''} quarter_end_date={quarter_end_date || ''} canCreateTopic={can_facilitate && !isCompleted && meeting.status === 'in_progress'} />
      case 'accountability':
        return <SectionAccountability action_items={[...action_items, ...(new_action_items || [])]} completedItems={completed_this_week || []} meetingId={meeting.id} teamId={meeting.team?.id} members={team_members} actionItemMembers={action_item_members} readOnly={isCompleted} canCreate={can_create_action_item === true} />
      case 'team_engagement':
        return <SectionTeamEngagement meeting={meeting} members={attendingMembers} readOnly={isCompleted} />
      case 'business_development':
        return <SectionBizDev meeting={meeting} members={team_members} readOnly={isCompleted} />
      case 'the_list':
        return <SectionTheList topics={topics} meetingId={meeting.id} teamId={meeting.team?.id} members={team_members} actionItemMembers={action_item_members} readOnly={isCompleted} inProgress={meeting.status === 'in_progress'} topicVoteCount={topic_vote_count || 0} meetingTopicResolutions={meeting_topic_resolutions || []} canFacilitate={can_facilitate} currentUserId={current_user?.id} />
      case 'projects':
        return <SectionProjects
          projects={(pageProps as any).team_projects || []}
          currentUserId={(pageProps as any).auth?.user?.id}
          currentUserIsAdmin={!!((pageProps as any).auth?.user?.is_admin_or_owner)}
        />
      case 'check_out':
        return <SectionCheckOut
          meeting={meeting}
          new_action_items={new_action_items}
          carriedOverItems={action_items.filter(i => i.status === 'carried_over')}
          meetingId={meeting.id}
          readOnly={isCompleted}
          canFacilitate={can_facilitate}
          teamMembers={team_members}
          meetingAttendees={meeting_attendees || []}
          currentUserId={current_user?.id}
          meetingTopicResolutions={meeting_topic_resolutions}
          sections={sections}
          meetingRatings={meeting_ratings}
          summaryRecipients={summary_recipients || []}
        />
      default:
        // Custom rows (and any unknown section_type) — render as a plain
        // timed section. The notes textarea below is the body.
        return null
    }
  }

  const renderSectionContent = () => {
    const phase = displayPhase
    if (!phase || !displaySection) return null

    const isBuiltIn = KNOWN_BUILT_IN_PHASES.has(phase)
    const body = isBuiltIn ? renderSectionBody(phase) : null

    return (
      <div className="space-y-6">
        {body}
        {/* Task #607: the Scoreboard keeps a list of carry-forward notes;
            every other section keeps the free-text notes box. */}
        {phase === 'scoreboard' ? (
          <ScoreboardSectionNotes
            meetingId={meeting.id}
            sectionId={displaySection.id}
            notes={displaySection.section_notes || []}
            readOnly={isCompleted}
          />
        ) : (
          <SectionNotes
            meetingId={meeting.id}
            sectionId={displaySection.id}
            initialNotes={displaySection.notes}
            readOnly={isCompleted}
            label={isBuiltIn ? 'Section Notes' : 'Notes'}
            placeholder={
              isBuiltIn
                ? 'Capture notes for this section (included in the meeting recap)…'
                : 'Capture notes for this section. Use the time to walk the team through it.'
            }
          />
        )}
      </div>
    )
  }

  const isViewingPast = viewingSectionId !== null && viewingSectionId !== currentSectionId
  const viewingSectionForBanner = viewingSectionId
    ? sections.find(s => s.id === viewingSectionId)
    : null

  const isCheckOutSection = displayPhase === 'check_out'
  const isScorecardHeader = meeting.status === 'in_progress' && displayPhase === 'scoreboard'
  const meetingDateLabel = formatDate(meeting.scheduled_date)

  return (
    <MeetingCreationContext.Provider value={meeting.status === 'in_progress' ? {
      meetingId: meeting.id, teamId: meeting.team?.id, teamMembers: team_members,
      actionItemMembers: action_item_members, currentUserId: current_user?.id,
    } : null}>
    <Layout
      globalNavVariant={isScorecardHeader ? 'scorecard' : 'default'}
      meetingHeader={isScorecardHeader ? (
        <QuickActionsToolbar
          meeting={meeting}
          teamMembers={team_members}
          actionItemMembers={action_item_members}
          canFacilitate={can_facilitate}
          isLastSection={is_last_section}
          currentUserId={current_user?.id}
          variant="scorecard"
          elapsedSeconds={totalMeetingTime}
          attendanceCount={viewerCount}
          meetingDateLabel={meetingDateLabel}
        />
      ) : undefined}
    >
      <Head title={`${meeting.display_name || meeting.agenda_template_name || 'Meeting'} ${formatDate(meeting.scheduled_date)} - Pinnacle`} />
        {meeting.status === 'in_progress' && !isScorecardHeader && (
        <QuickActionsToolbar
          meeting={meeting}
          teamMembers={team_members}
          actionItemMembers={action_item_members}
          canFacilitate={can_facilitate}
          isLastSection={is_last_section}
          currentUserId={current_user?.id}
        />
      )}

      {!isScorecardHeader && <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-sm">
          <Link href="/meetings" className="text-text-muted hover:text-text-primary">Meetings</Link>
          <span className="text-text-muted">/</span>
          <span className="text-text-primary">{formatDate(meeting.scheduled_date)}</span>
        </div>
        <div className="flex items-center gap-3">
          {meeting.status === 'in_progress' && (
            <>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal/5 rounded-lg">
                <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-mono font-semibold text-text-primary">
                  {Math.floor(totalMeetingTime / 3600) > 0
                    ? `${Math.floor(totalMeetingTime / 3600)}:${Math.floor((totalMeetingTime % 3600) / 60).toString().padStart(2, '0')}:${(totalMeetingTime % 60).toString().padStart(2, '0')}`
                    : formatTime(totalMeetingTime)
                  }
                </span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal/5 rounded-lg">
                <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-text-muted">{viewerCount}</span>
              </div>
              <span className="px-3 py-1 bg-pinnacle-red/10 text-pinnacle-red rounded-full text-sm font-medium">
                In Progress
              </span>
            </>
          )}
          {isCompleted && (
            <>
              {meeting.rating && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal/5 rounded-lg">
                  <span className="text-sm text-text-muted">Rating: {meeting.rating}/6</span>
                </div>
              )}
              <span className="px-3 py-1 bg-[#15532E]/10 text-[#15532E] rounded-full text-sm font-medium">
                Completed
              </span>
            </>
          )}
        </div>
      </div>}

      {isCompleted && meeting.recap_skip_reason && (current_user?.role === 'admin' || current_user?.role === 'account_owner') && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-amber-800">{meeting.recap_skip_reason}</p>
        </div>
      )}

      <div>
          {meeting.status === 'scheduled' ? (
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-2xl font-semibold text-text-primary font-heading">{meeting.team?.name || 'Team'}</span>
                  {editingName ? (
                    <form onSubmit={(e) => { e.preventDefault(); handleSaveDisplayName() }} className="inline-flex items-center gap-2">
                      <input
                        type="text"
                        value={nameValue}
                        onChange={(e) => setNameValue(e.target.value)}
                        className="text-2xl font-semibold text-text-primary font-heading border-b-2 border-pinnacle-red bg-transparent outline-none px-1 w-64 text-center"
                        autoFocus
                        onBlur={handleSaveDisplayName}
                      />
                    </form>
                  ) : (
                    <button
                      onClick={() => { if (can_facilitate) setEditingName(true) }}
                      className={`text-2xl font-semibold text-text-primary font-heading ${can_facilitate ? 'hover:bg-stone/10 rounded px-1 cursor-pointer' : ''}`}
                      title={can_facilitate ? 'Click to edit meeting name' : undefined}
                    >
                      {meeting.display_name || meeting.agenda_template_name || 'Meeting'}
                    </button>
                  )}
                </div>
                <p className="text-text-muted">
                  Facilitated by {meeting.facilitator.full_name} · {formatDate(meeting.scheduled_date)}
                </p>
              </div>
              <MeetingSetup
                key={sections.map(s => s.id).join(',')}
                meetingId={meeting.id}
                meetingDisplayName={meeting.display_name}
                sections={sections}
                templates={templates || []}
                availableSections={available_sections || []}
                currentTemplateId={meeting.agenda_template_id}
                canFacilitate={can_facilitate}
                canSaveAsTemplate={!!can_save_as_template}
                canStart={can_start}
                attendees={meeting_attendees || []}
              />
            </div>
          ) : (
            <div className={`flex gap-6 ${displayPhase === 'set_the_stage' ? 'flex-col-reverse lg:flex-row' : ''}`}>
              <aside className={`flex-shrink-0 ${displayPhase === 'set_the_stage' ? 'w-full lg:w-64' : 'w-64'}`}>
                <div className="sticky top-6 space-y-4">
                  <div className="bg-white rounded-xl shadow-card p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide">Agenda</h3>
                    </div>
                    <nav className="space-y-1">
                      {sections.map((section) => {
                        if (section.is_heading) {
                          return (
                            <div
                              key={section.id}
                              className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-text-muted"
                            >
                              {section.display_name || section.title}
                            </div>
                          )
                        }
                        const sectionType = section.section_type
                        const status = getSectionStatusForSection(section)
                        const isCurrent = status === 'current' && !isViewingPast
                        const isSectionCompleted = status === 'completed'
                        const isFuture = status === 'upcoming' && meeting.status !== 'completed'
                        const isViewing = meeting.status === 'completed'
                          ? section.id === displaySectionId
                          : viewingSectionId === section.id
                        const isRunning = section ? (runningSections[section.id] || false) : false
                        const facilitatorCanJump = can_facilitate && meeting.status === 'in_progress'
                        const isClickable = facilitatorCanJump || !isFuture

                        return (
                          <button
                            key={section.id}
                            onClick={() => isClickable && handleSidebarClick(section)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                              isCurrent || isViewing
                                ? 'bg-pinnacle-red/5 border-l-[3px] border-l-pinnacle-red text-text-primary'
                                : isSectionCompleted
                                  ? `text-text-primary ${isClickable ? 'hover:bg-stone/10 cursor-pointer' : ''}`
                                  : isFuture
                                    ? `text-text-primary ${facilitatorCanJump ? 'hover:bg-stone/10 cursor-pointer' : 'cursor-default'}`
                                    : 'text-text-primary'
                            }`}
                            disabled={!isClickable}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {isSectionCompleted && !isViewing && (
                                  <span className="text-[#15532E] text-sm font-bold">✓</span>
                                )}
                                {(isCurrent || isViewing) && isRunning && (
                                  <span className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse bg-pinnacle-red" />
                                )}
                                <p className="text-sm font-medium truncate">
                                  {sectionRowDisplayName(section)}
                                </p>
                              </div>
                              {section && meeting.status === 'in_progress' && (isCurrent || isSectionCompleted) ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className={`text-xs font-mono font-medium ${
                                    (sectionTimes[section.id] || 0) > section.duration_minutes * 60
                                      ? 'text-pinnacle-red'
                                      : isRunning ? 'text-text-primary' : 'text-text-muted'
                                  }`}>
                                    {formatTime(sectionTimes[section.id] || 0)}
                                  </span>
                                  <span className="text-xs text-text-muted">/ {section.duration_minutes}m</span>
                                  {isRunning && (
                                    <svg className="w-3 h-3 text-pinnacle-red ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  )}
                                  {!isRunning && isSectionCompleted && (
                                    <svg className="w-3 h-3 text-text-muted ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                    </svg>
                                  )}
                                </div>
                              ) : section && meeting.status === 'completed' && (sectionTimes[section.id] || 0) > 0 ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className={`text-xs font-mono font-medium ${
                                    (sectionTimes[section.id] || 0) > section.duration_minutes * 60
                                      ? 'text-pinnacle-red'
                                      : 'text-text-muted'
                                  }`}>
                                    {formatTime(sectionTimes[section.id] || 0)}
                                  </span>
                                  <span className="text-xs text-text-muted">/ {section.duration_minutes}m</span>
                                </div>
                              ) : (
                                <p className="text-xs text-text-muted mt-0.5">
                                  {section ? `${section.duration_minutes} min` : sectionDuration(sectionType)}
                                </p>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </nav>
                  </div>

                  {meeting_attendees && meeting_attendees.length > 0 && (
                    <div className="bg-white rounded-xl shadow-card p-4">
                      <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-3">Attendees</h3>
                      <div className="flex flex-wrap gap-2">
                        {/* allow-unsorted-people: do NOT alphabetize. The
                            sidebar avatar strip keeps the facilitator's custom
                            attendee order set in meeting setup. */}
                        {meeting_attendees.map((attendee) => {
                          const isConnected = meeting.status === 'in_progress' && connectedUserIds.includes(attendee.user.id)
                          return (
                            <UserAvatar
                              key={attendee.id}
                              user={attendee.user}
                              size="sm"
                              variant="primary"
                              online={isConnected}
                              dimmed={!attendee.attending}
                            />
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </aside>

              <main className="min-w-0 flex-1">
                {meeting.status === 'completed' ? (
                  <div className="space-y-4">
                    {(() => {
                      // Task #607: the Scoreboard's notes live in section_notes.
                      const sectionsWithNotes = sections.filter(
                        s => (s.notes ?? '').trim().length > 0 || (s.section_notes?.length ?? 0) > 0
                      )
                      if (sectionsWithNotes.length === 0) return null
                      return (
                        <details className="bg-white border border-stone/10 rounded-xl p-5 group" open={false}>
                          <summary className="flex items-center justify-between cursor-pointer list-none">
                            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary">
                              Section Notes ({sectionsWithNotes.length})
                            </h4>
                            <svg
                              className="w-4 h-4 text-text-muted transition-transform group-open:rotate-180"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </summary>
                          <div className="mt-4 space-y-3">
                            {sectionsWithNotes.map(s => (
                              <div key={s.id} className="py-2 border-b border-stone/5 last:border-0">
                                <div className="text-sm font-semibold text-text-primary">
                                  {sectionRowDisplayName(s)}
                                </div>
                                {(s.section_notes?.length ?? 0) > 0 && (
                                  <ul className="mt-1 list-disc pl-5 space-y-1">
                                    {[...(s.section_notes ?? [])]
                                      .sort((a, b) => (a.position - b.position) || (a.id - b.id))
                                      .map(n => (
                                        <li key={n.id} className="text-sm text-text-primary whitespace-pre-wrap">{n.body}</li>
                                      ))}
                                  </ul>
                                )}
                                {(s.notes ?? '').trim().length > 0 && (
                                  <p className="text-sm text-text-primary whitespace-pre-wrap mt-1">{s.notes}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      )
                    })()}
                    <div className="bg-white rounded-xl shadow-card p-6">
                      <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone/10">
                        <p className="text-sm text-text-muted">
                          Section {displaySectionIndex + 1} of {timedSections.length}
                          {displaySection ? `: ${displaySection.display_name || displaySection.title}` : ''}
                        </p>
                        <Link href="/meetings" className="text-sm text-pinnacle-red hover:underline font-medium">
                          Back to Meetings
                        </Link>
                      </div>

                      <SectionErrorBoundary key={displaySectionId ?? 'none'}>
                        {renderSectionContent()}
                      </SectionErrorBoundary>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timeWarning && (
                      <div className={`rounded-lg px-4 py-3 flex items-center justify-between animate-pulse ${
                        timeWarning.type === 'over'
                          ? 'bg-pinnacle-red/10 border border-pinnacle-red/30'
                          : 'bg-[#E5A100]/10 border border-[#E5A100]/30'
                      }`}>
                        <div className="flex items-center gap-2">
                          <svg className={`w-5 h-5 flex-shrink-0 ${timeWarning.type === 'over' ? 'text-pinnacle-red' : 'text-[#E5A100]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <p className={`text-sm font-semibold ${timeWarning.type === 'over' ? 'text-pinnacle-red' : 'text-[#E5A100]'}`}>
                            {timeWarning.type === 'over'
                              ? `${timeWarning.sectionName} is over time!`
                              : `${timeWarning.sectionName} — less than 30 seconds remaining`
                            }
                          </p>
                        </div>
                        <button
                          onClick={dismissWarning}
                          className={`text-sm font-medium hover:underline ${timeWarning.type === 'over' ? 'text-pinnacle-red' : 'text-[#E5A100]'}`}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    {isViewingPast && viewingSectionForBanner && (
                      <div className="bg-stone/10 border border-stone/20 rounded-lg px-4 py-3 flex items-center justify-between">
                        <p className="text-sm text-text-primary">
                          Viewing completed section: <span className="font-medium">{viewingSectionForBanner.display_name || viewingSectionForBanner.title}</span>
                        </p>
                        <button
                          onClick={() => setViewingSectionId(null)}
                          className="text-sm text-pinnacle-red hover:underline font-medium"
                        >
                          Back to current section
                        </button>
                      </div>
                    )}

                    <div className={`bg-white rounded-xl shadow-card p-6 ${
                      displaySection && isOverTime(displaySection) ? 'ring-2 ring-pinnacle-red/30' : ''
                    }`}>
                      <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone/10">
                        <div className="flex items-center gap-3">
                          <p className="text-sm text-text-muted">
                            Section {displaySectionIndex + 1} of {timedSections.length}
                            {displaySection ? `: ${displaySection.display_name || displaySection.title}` : ''}
                          </p>
                          {currentSection && !isViewingPast && (
                            <>
                              <span className="text-text-muted">|</span>
                              <div className="flex items-center gap-1.5 text-sm">
                                {runningSections[currentSection.id] && (
                                  <span className="inline-block w-2 h-2 rounded-full animate-pulse bg-pinnacle-red" />
                                )}
                                <span className={`font-mono font-medium ${
                                  isOverTime(currentSection) ? 'text-pinnacle-red' : 'text-text-primary'
                                }`}>
                                  {formatTime(sectionTimes[currentSection.id] || 0)}
                                </span>
                                <span className="text-text-muted">
                                  / {currentSection.duration_minutes}:00
                                </span>
                              </div>
                            </>
                          )}
                          {displaySection && isOverTime(displaySection) && (
                            <span className="px-2 py-0.5 bg-pinnacle-red/10 text-pinnacle-red text-xs font-bold uppercase rounded">
                              Over Time
                            </span>
                          )}
                        </div>
                        {meeting.status === 'in_progress' && (
                          <div className="flex items-center gap-2 text-sm text-text-muted">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {viewerCount} viewing
                          </div>
                        )}
                      </div>

                      <SectionErrorBoundary key={displaySectionId ?? 'none'}>
                        {renderSectionContent()}
                      </SectionErrorBoundary>
                    </div>

                    {!isViewingPast && can_facilitate && (
                      <div className="flex justify-end mt-8 pt-4 border-t border-stone">
                        {isCheckOutSection ? (
                          <button
                            onClick={handleEndMeeting}
                            className="px-6 py-3 bg-pinnacle-red text-white font-semibold rounded-md min-h-[44px] flex items-center gap-2 hover:bg-pinnacle-red-dark transition-colors"
                          >
                            End Meeting
                          </button>
                        ) : (
                          <button
                            onClick={handleAdvanceSection}
                            className="px-6 py-3 bg-pinnacle-red text-white font-semibold rounded-md min-h-[44px] flex items-center gap-2 hover:bg-pinnacle-red-dark transition-colors"
                          >
                            Next Section →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </main>
            </div>
          )}
        </div>
      {meeting.status === 'in_progress' && (
        <>
          <div aria-hidden="true" className="h-20" />
          <div
            role="status"
            aria-live="polite"
            className={`fixed bottom-0 left-0 right-0 z-40 border-t shadow-elevated ${
              remainingSeconds <= 0
                ? 'bg-pinnacle-red text-white border-pinnacle-red-dark'
                : remainingSeconds <= 120
                  ? 'bg-status-off-track-bg text-status-off-track border-status-off-track/30'
                  : remainingSeconds <= 300
                    ? 'bg-status-at-risk-bg text-status-at-risk border-status-at-risk/30'
                    : 'bg-white text-text-primary border-stone'
            }`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium uppercase tracking-wide truncate">
                  {remainingSeconds <= 0 ? 'Over time' : 'Time remaining'}
                </span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className={`font-mono font-bold tabular-nums ${
                  remainingSeconds <= 0 ? 'text-3xl' : 'text-2xl'
                }`}>
                  {formatCountdown(remainingSeconds)}
                </span>
                <span className={`text-xs hidden sm:inline ${
                  remainingSeconds <= 0 ? 'text-text-on-dark' : ''
                }`}>
                  of {formatCountdown(totalAllottedSeconds)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
    </MeetingCreationContext.Provider>
  )
}
